import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./ShortTermMemoryTrainingGame.css";

/* =====================================================
   CARD DATA
   ===================================================== */

const SUITS = [
  {
    key: "spades",
    symbol: "♠",
    color: "black",
  },
  {
    key: "hearts",
    symbol: "♥",
    color: "red",
  },
  {
    key: "clubs",
    symbol: "♣",
    color: "black",
  },
  {
    key: "diamonds",
    symbol: "♦",
    color: "red",
  },
];

const RANKS = [
  "A",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
  "J",
  "Q",
  "K",
];

/* =====================================================
   CREATE DECK
   ===================================================== */

const createDeck = () => {
  const deck = [];

  SUITS.forEach((suit) => {
    RANKS.forEach((rank) => {
      deck.push({
        id: `${suit.key}-${rank}`,
        rank,
        suit: suit.symbol,
        color: suit.color,
      });
    });
  });

  return deck;
};

/* =====================================================
   SHUFFLE
   ===================================================== */

const shuffleArray = (array) => {
  const copied = [...array];

  for (let i = copied.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));

    [copied[i], copied[j]] = [
      copied[j],
      copied[i],
    ];
  }

  return copied;
};

const getRandomCards = (count) => {
  return shuffleArray(createDeck()).slice(0, count);
};

/* =====================================================
   SIDE FLOATING CARD
   ===================================================== */

const SideFloatingCard = ({
  card,
  className = "",
}) => {
  if (!card) return null;

  return (
    <div
      className={`floating-playing-card ${className} ${
        card.color === "red" ? "floating-red-card" : ""
      }`}
    >
      <div className="floating-card-top">
        {card.rank}
        {card.suit}
      </div>

      <div className="floating-card-center">
        {card.suit}
      </div>

      <div className="floating-card-bottom">
        {card.rank}
        {card.suit}
      </div>
    </div>
  );
};

/* =====================================================
   SIDE CARD DECORATION
   ===================================================== */

const SideCards = () => {
  const sideCards = [
    {
      rank: "3",
      suit: "♠",
      color: "black",
    },
    {
      rank: "7",
      suit: "♥",
      color: "red",
    },
    {
      rank: "5",
      suit: "♣",
      color: "black",
    },
    {
      rank: "9",
      suit: "♦",
      color: "red",
    },
  ];

  return (
    <>
      <SideFloatingCard
        card={sideCards[0]}
        className="floating-card-left-top"
      />

      <SideFloatingCard
        card={sideCards[1]}
        className="floating-card-right-top"
      />

      <SideFloatingCard
        card={sideCards[2]}
        className="floating-card-left-bottom"
      />

      <SideFloatingCard
        card={sideCards[3]}
        className="floating-card-right-bottom"
      />
    </>
  );
};

/* =====================================================
   MAIN COMPONENT
   ===================================================== */

const ShortTermMemoryTrainingGame = ({
  initialCardCount = 3,
  initialDisplayTime = 5,
  onExit,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  // One trainingSessionId is kept for the complete training run.
  // All rounds from one Start Training are saved in one MongoDB document.
  const trainingSessionIdRef = useRef(
    crypto.randomUUID()
  );

  /*
    Values coming from:
    ShortTermMemoryTraining -> Start Training
  */

  const routeCardCount =
    location.state?.cardCount ??
    initialCardCount;

  const routeDisplayTime =
    location.state?.displayTime ??
    initialDisplayTime;

  const [cardCount, setCardCount] =
    useState(routeCardCount);

  const [displayTime, setDisplayTime] =
    useState(routeDisplayTime);

  const [round, setRound] = useState(1);

  const [phase, setPhase] =
    useState("dealing");

  const [currentSequence, setCurrentSequence] =
    useState([]);

  const [userSequence, setUserSequence] =
    useState([]);

  const [countdown, setCountdown] =
    useState(routeDisplayTime);

  const [result, setResult] =
    useState(null);

  const [isSaving, setIsSaving] =
    useState(false);

  const [saveError, setSaveError] =
    useState("");

  const [checkingAccess, setCheckingAccess] =
    useState(true);

  const [trainingAccess, setTrainingAccess] =
    useState(false);

  const [dealingIndex, setDealingIndex] =
    useState(-1);

  const audioContextRef =
    useRef(null);

  const timersRef =
    useRef([]);

  /* =====================================================
     CLEAR TIMERS
     ===================================================== */

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((timer) => {
      clearTimeout(timer);
      clearInterval(timer);
    });

    timersRef.current = [];
  }, []);

  /* =====================================================
     SOUND
     ===================================================== */

  const getAudioContext = () => {
    if (!audioContextRef.current) {
      const AudioContext =
        window.AudioContext ||
        window.webkitAudioContext;

      if (!AudioContext) {
        return null;
      }

      audioContextRef.current =
        new AudioContext();
    }

    const ctx =
      audioContextRef.current;

    if (ctx.state === "suspended") {
      ctx.resume();
    }

    return ctx;
  };

  const playTone = (
    frequency,
    duration = 0.12,
    type = "sine",
    volume = 0.06
  ) => {
    const ctx = getAudioContext();

    if (!ctx) return;

    const oscillator =
      ctx.createOscillator();

    const gain =
      ctx.createGain();

    oscillator.type = type;
    oscillator.frequency.value =
      frequency;

    gain.gain.setValueAtTime(
      volume,
      ctx.currentTime
    );

    gain.gain.exponentialRampToValueAtTime(
      0.001,
      ctx.currentTime + duration
    );

    oscillator.connect(gain);
    gain.connect(ctx.destination);

    oscillator.start();

    oscillator.stop(
      ctx.currentTime + duration
    );
  };

  const playDealSound = () => {
    playTone(
      520,
      0.08,
      "triangle",
      0.05
    );

    setTimeout(() => {
      playTone(
        760,
        0.06,
        "triangle",
        0.04
      );
    }, 45);
  };

  const playTickSound = () => {
    playTone(
      800,
      0.07,
      "sine",
      0.035
    );
  };

  const playSuccessSound = () => {
    playTone(
      520,
      0.1,
      "sine",
      0.06
    );

    setTimeout(() => {
      playTone(
        700,
        0.1,
        "sine",
        0.06
      );
    }, 100);

    setTimeout(() => {
      playTone(
        900,
        0.16,
        "sine",
        0.06
      );
    }, 200);
  };

  const playErrorSound = () => {
    playTone(
      260,
      0.14,
      "sawtooth",
      0.045
    );

    setTimeout(() => {
      playTone(
        180,
        0.2,
        "sawtooth",
        0.04
      );
    }, 120);
  };

  /* =====================================================
     START ROUND
     ===================================================== */

  const startRound = useCallback(
    (
      count = cardCount,
      seconds = displayTime
    ) => {
      clearTimers();

      const newSequence =
        getRandomCards(count);

      setCurrentSequence(
        newSequence
      );

      setUserSequence([]);

      setResult(null);

      setCountdown(seconds);

      setDealingIndex(-1);

      setPhase("dealing");

      /* First card */

      const firstTimer =
        setTimeout(() => {
          setDealingIndex(0);

          playDealSound();
        }, 300);

      timersRef.current.push(
        firstTimer
      );

      /* Remaining cards */

      for (
        let i = 1;
        i < newSequence.length;
        i++
      ) {
        const timer = setTimeout(() => {
          setDealingIndex(i);

          playDealSound();
        }, 700 * i + 300);

        timersRef.current.push(
          timer
        );
      }

      /* Show phase */

      const showTimer =
        setTimeout(
          () => {
            setDealingIndex(
              newSequence.length - 1
            );

            setPhase("showing");
          },
          700 *
            newSequence.length +
            500
        );

      timersRef.current.push(
        showTimer
      );

      /* Countdown */

      const countdownStart =
        700 *
          newSequence.length +
        550;

      const countdownTimer =
        setTimeout(() => {
          let remaining = seconds;

          setCountdown(
            remaining
          );

          const interval =
            setInterval(() => {
              remaining -= 1;

              setCountdown(
                remaining
              );

              if (
                remaining <= 3 &&
                remaining > 0
              ) {
                playTickSound();
              }

              if (
                remaining <= 0
              ) {
                clearInterval(
                  interval
                );

                setPhase("input");
              }
            }, 1000);

          timersRef.current.push(
            interval
          );
        }, countdownStart);

      timersRef.current.push(
        countdownTimer
      );
    },
    [
      cardCount,
      displayTime,
      clearTimers,
    ]
  );

  /* =====================================================
     INITIAL START
     ===================================================== */

  useEffect(() => {
    let isMounted = true;

    const checkTrainingAccess = async () => {
      try {
        const email = getUserEmail();

        if (!email) {
          if (isMounted) {
            setTrainingAccess(false);
          }
          return;
        }

        const response = await fetch(
          `/api/whitelist-user/training-access/${encodeURIComponent(email)}`
        );

        const data = await response.json().catch(() => ({}));

        if (!response.ok || data?.allowed !== true) {
          if (isMounted) {
            setTrainingAccess(false);
          }
          return;
        }

        if (!isMounted) return;

        setTrainingAccess(true);

        startRound(
          routeCardCount,
          routeDisplayTime
        );
      } catch (error) {
        console.error("🔥 Training access check failed:", error);

        if (isMounted) {
          setTrainingAccess(false);
        }
      } finally {
        if (isMounted) {
          setCheckingAccess(false);
        }
      }
    };

    checkTrainingAccess();

    return () => {
      isMounted = false;
      clearTimers();

      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  /* =====================================================
     SELECT CARD
     ===================================================== */

  const handleCardSelect = (
    card
  ) => {
    if (phase !== "input") {
      return;
    }

    if (
      userSequence.length >=
      cardCount
    ) {
      return;
    }

    setUserSequence(
      (previous) => [
        ...previous,
        card,
      ]
    );
  };

  /* =====================================================
     UNDO
     ===================================================== */

  const handleUndo = () => {
    if (phase !== "input") {
      return;
    }

    setUserSequence(
      (previous) =>
        previous.slice(0, -1)
    );
  };

  /* =====================================================
     CLEAR
     ===================================================== */

  const handleClear = () => {
    if (phase !== "input") {
      return;
    }

    setUserSequence([]);
  };

  /* =====================================================
     EXIT / REAL TEST
     ===================================================== */

  const handleExit = () => {
    clearTimers();

    // Go back to the Training selection page.
    navigate("/dashboard-login");
  };

  const handleRealTest = () => {
    clearTimers();

    // Open the original Short Term Memory real test.
    navigate("/short-term-memory");
  };

  /* =====================================================
     SAVE TRAINING SESSION
     ===================================================== */

  const getUserEmail = () => {
    // Prefer email passed through router state.
    if (location.state?.email) {
      return String(location.state.email).trim().toLowerCase();
    }

    // Fallback to common localStorage keys used by the website.
    const possibleKeys = [
      "email",
      "userEmail",
      "user_email",
      "user",
      "userData",
    ];

    for (const key of possibleKeys) {
      const value = localStorage.getItem(key);

      if (!value) continue;

      try {
        const parsed = JSON.parse(value);

        if (parsed?.email) {
          return String(parsed.email).trim().toLowerCase();
        }

        if (parsed?.user?.email) {
          return String(parsed.user.email).trim().toLowerCase();
        }
      } catch {
        // Plain string value
        if (value.includes("@")) {
          return value.trim().toLowerCase();
        }
      }
    }

    return "";
  };

  const cardSequenceToString = (cards = []) => {
    return cards
      .map((card) => `${card.rank}${card.suit}`)
      .join(",");
  };

  const saveTrainingSession = async (isCorrect) => {
    const email = getUserEmail();

    if (!email) {
      setSaveError("User email not found. Training result was not saved.");
      return false;
    }

    const payload = {
      email,
      level: round,
      trainingSessionId: trainingSessionIdRef.current,
      sessions: [
        {
          level: round,
          cardCount,
          displayTime,
          correctSequence: cardSequenceToString(currentSequence),
          userInput: cardSequenceToString(userSequence),
          correct: isCorrect,
        },
      ],
    };

    try {
      setIsSaving(true);
      setSaveError("");

      const response = await fetch(
        "/api/save-short-term-memory-training",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data?.error ||
            data?.message ||
            "Failed to save training session"
        );
      }

      return true;
    } catch (error) {
      console.error(
        "🔥 Short Term Memory Training Save Error:",
        error
      );

      setSaveError(
        error.message ||
          "Failed to save training session"
      );

      return false;
    } finally {
      setIsSaving(false);
    }
  };

  /* =====================================================
     SUBMIT
     ===================================================== */

  const handleSubmit = async () => {
    if (phase !== "input") {
      return;
    }

    if (
      userSequence.length !==
      cardCount
    ) {
      return;
    }

    const isCorrect =
      userSequence.every(
        (card, index) =>
          card.id ===
          currentSequence[
            index
          ].id
      );

    const saved = await saveTrainingSession(isCorrect);

    // Do not move to the result screen when the backend rejected
    // the session (for example, the user is not whitelisted).
    if (!saved) {
      return;
    }

    if (isCorrect) {
      setResult("correct");

      setPhase("result");

      playSuccessSound();
    } else {
      setResult("wrong");

      setPhase("result");

      playErrorSound();
    }
  };

  /* =====================================================
     NEXT ROUND
     ===================================================== */

  const handleContinue = () => {
    const nextCardCount =
      cardCount + 1;

    setCardCount(
      nextCardCount
    );

    setRound(
      (previous) =>
        previous + 1
    );

    startRound(
      nextCardCount,
      displayTime
    );
  };

  /* =====================================================
     RETRY
     ===================================================== */

  const handleRetry = () => {
    startRound(
      cardCount,
      displayTime
    );
  };

  /* =====================================================
     CHANGE CARDS
     ===================================================== */

  const handleChangeCards = (
    newCount
  ) => {
    setCardCount(newCount);

    setPhase("settings");

    setResult(null);
  };

  const handleContinueFromSettings =
    () => {
      setRound(
        (previous) =>
          previous + 1
      );

      startRound(
        cardCount,
        displayTime
      );
    };

  /* =====================================================
     CHANGE TIME
     ===================================================== */

  const handleTimeChange = (
    time
  ) => {
    setDisplayTime(time);
  };

  /* =====================================================
     DECK
     ===================================================== */

  const deck = useMemo(
    () => createDeck(),
    []
  );

  /* =====================================================
     PLAYING CARD
     ===================================================== */

  const PlayingCard = ({
    card,
    small = false,
    selected = false,
    onClick,
    disabled = false,
  }) => {
    if (!card) {
      return null;
    }

    return (
      <button
        type="button"
        className={`game-card ${
          card.color === "red"
            ? "red-card"
            : ""
        } ${
          small
            ? "small-card"
            : ""
        } ${
          selected
            ? "selected-card"
            : ""
        }`}
        onClick={onClick}
        disabled={disabled}
      >
        <span className="card-corner top-corner">
          {card.rank}
          {card.suit}
        </span>

        <span className="card-center-suit">
          {card.suit}
        </span>

        <span className="card-corner bottom-corner">
          {card.rank}
          {card.suit}
        </span>
      </button>
    );
  };

  /* =====================================================
     FULL PAGE WRAPPER
     ===================================================== */

  const Page = ({
    children,
    className = "",
  }) => {
    return (
      <div
        className={`training-game-page ${className}`}
      >
        <SideCards />

        <div className="training-game-container">
          {children}
        </div>
      </div>
    );
  };

  /* =====================================================
     ACCESS CHECK UI
     IMPORTANT:
     Do not render the game until access is verified.
     ===================================================== */

  if (checkingAccess) {
    return (
      <div className="training-access-page">
        <div className="training-access-card">
          <div className="training-access-icon">
            🔐
          </div>

          <h1>Checking Training Access...</h1>

          <p>
            Please wait while we verify your
            Forge Your Peak membership.
          </p>
        </div>
      </div>
    );
  }

  if (!trainingAccess) {
    return (
      <div className="training-access-page">
        <div className="training-access-card">

          <div className="training-access-icon">
            🔒
          </div>

          <h1>
            Training Module
          </h1>

          <p>
            Short Term Memory Training is
            available exclusively for
            Forge Your Peak - Ascend members.
          </p>

          <button
            type="button"
            className="ascend-access-btn"
            onClick={() =>
              navigate("/ascend-payment")
            }
          >
            🚀 Get Ascend Access
          </button>

          <button
            type="button"
            className="back-home-btn"
            onClick={() =>
              navigate("/")
            }
          >
            ← Back to Home
          </button>

        </div>
      </div>
    );
  }


  /* =====================================================
     DEALING SCREEN
     ===================================================== */

  if (phase === "dealing") {
    return (
      <Page>

        <TrainingHeader
          round={round}
          cardCount={cardCount}
          onExit={handleExit}
          onRealTest={handleRealTest}
        />

        <div className="dealing-area">

          {/* DECK */}

          <div className="deck-wrapper">

            <div className="deck-card deck-card-back">
              <div className="deck-pattern">
                ♠
              </div>
            </div>

            <div className="deck-card deck-card-back deck-two">
              <div className="deck-pattern">
                ♠
              </div>
            </div>

            <div className="deck-card deck-card-back deck-three">
              <div className="deck-pattern">
                ♠
              </div>
            </div>

          </div>

          {/* DEALING CARDS */}

          <div className="dealing-cards">

            {currentSequence.map(
              (card, index) => (
                <div
                  key={card.id}
                  className={`deal-card ${
                    index <=
                    dealingIndex
                      ? "deal-card-visible"
                      : ""
                  }`}
                >
                  <PlayingCard
                    card={card}
                  />
                </div>
              )
            )}

          </div>

        </div>

        <div className="game-message blue-message">
          <strong>
            Dealing cards...
          </strong>

          <span>
            Watch carefully and remember
            the sequence.
          </span>
        </div>

      </Page>
    );
  }

  /* =====================================================
     SHOWING SCREEN
     ===================================================== */

  if (phase === "showing") {
    return (
      <Page>

        <TrainingHeader
          round={round}
          cardCount={cardCount}
          onExit={handleExit}
          onRealTest={handleRealTest}
        />

        <div className="countdown-circle">
          <strong>
            {countdown}
          </strong>

          <span>
            seconds
          </span>
        </div>

        <div className="visible-cards">

          {currentSequence.map(
            (card) => (
              <PlayingCard
                key={card.id}
                card={card}
              />
            )
          )}

        </div>

        <div className="game-message blue-message">
          <strong>
            Remember the sequence
          </strong>

          <span>
            The cards will disappear soon.
          </span>
        </div>

      </Page>
    );
  }

  /* =====================================================
     INPUT SCREEN
     ===================================================== */

  if (phase === "input") {
    return (
      <Page className="input-page">

        <TrainingHeader
          round={round}
          cardCount={cardCount}
          onExit={handleExit}
          onRealTest={handleRealTest}
        />

        <div className="game-message yellow-message">

          <strong>
            Recreate the sequence
          </strong>

          <span>
            Select the cards in the same
            order as shown.
          </span>

        </div>

        {/* USER SEQUENCE */}

        <div className="sequence-area">

          <h3>
            Your Sequence
          </h3>

          <div className="sequence-slots">

            {Array.from({
              length: cardCount,
            }).map(
              (_, index) => {
                const card =
                  userSequence[
                    index
                  ];

                return (
                  <div
                    key={index}
                    className="sequence-slot"
                  >
                    {card ? (
                      <PlayingCard
                        card={card}
                        small
                      />
                    ) : (
                      <span>
                        {index + 1}
                      </span>
                    )}
                  </div>
                );
              }
            )}

          </div>

        </div>

        {/* CARD SELECTOR */}

        <div className="card-selector">

          {SUITS.map(
            (suit) => (
              <div
                key={suit.key}
                className="suit-row"
              >

                <div
                  className={`suit-label ${
                    suit.color ===
                    "red"
                      ? "red-card-text"
                      : ""
                  }`}
                >
                  {suit.symbol}
                </div>

                <div className="rank-buttons">

                  {RANKS.map(
                    (rank) => {
                      const card =
                        deck.find(
                          (item) =>
                            item.rank ===
                              rank &&
                            item.suit ===
                              suit.symbol
                        );

                      return (
                        <button
                          type="button"
                          key={card.id}
                          className={`rank-btn ${
                            suit.color ===
                            "red"
                              ? "red-card-text"
                              : ""
                          }`}
                          onClick={() =>
                            handleCardSelect(
                              card
                            )
                          }
                          disabled={
                            userSequence.length >=
                            cardCount
                          }
                        >
                          {rank}
                        </button>
                      );
                    }
                  )}

                </div>

              </div>
            )
          )}

        </div>

        {/* ACTIONS */}

        <div className="input-actions">

          <button
            type="button"
            className="secondary-btn"
            onClick={handleClear}
          >
            🗑 Clear
          </button>

          <button
            type="button"
            className="secondary-btn"
            onClick={handleUndo}
          >
            ↶ Undo
          </button>

          <button
            type="button"
            className="submit-btn"
            onClick={handleSubmit}
            disabled={
              userSequence.length !== cardCount ||
              isSaving
            }
          >
            {isSaving
              ? "Saving..."
              : "➤ Submit"}
          </button>

        </div>

        {saveError && (
          <div className="game-message red-message">
            <strong>Unable to save training</strong>
            <span>{saveError}</span>
          </div>
        )}

      </Page>
    );
  }

  /* =====================================================
     RESULT SCREEN
     ===================================================== */

  if (phase === "result") {
    const isCorrect =
      result === "correct";

    return (
      <Page className="result-page">

        {isCorrect ? (
          <>
            <div className="result-icon success-icon">
              ✓
            </div>

            <h1 className="result-title success-title">
              Correct!
            </h1>

            <h3>
              Great Memory!
            </h3>

            <p className="result-description">
              You remembered the sequence.
            </p>

            <div className="result-sequence">

              <h4>
                Your Sequence
              </h4>

              <div className="result-cards">

                {userSequence.map(
                  (card) => (
                    <PlayingCard
                      key={card.id}
                      card={card}
                      small
                    />
                  )
                )}

              </div>

            </div>

            <div className="next-round-box">

              <span>
                🏆 Next Round
              </span>

              <strong>
                {cardCount + 1} Cards
                {" • "}
                {displayTime} sec
              </strong>

            </div>

            <button
              type="button"
              className="continue-btn"
              onClick={
                handleContinue
              }
            >
              ▶ Continue Training
            </button>
          </>
        ) : (
          <>
            <div className="result-icon error-icon">
              ×
            </div>

            <h1 className="result-title error-title">
              Try Again
            </h1>

            <h3>
              Not quite right. Keep going!
            </h3>

            <div className="comparison-area">

              <div>
                <h4>
                  Your Sequence
                </h4>

                <div className="result-cards">

                  {userSequence.map(
                    (card) => (
                      <PlayingCard
                        key={card.id}
                        card={card}
                        small
                      />
                    )
                  )}

                </div>
              </div>

              <div>
                <h4>
                  Correct Sequence
                </h4>

                <div className="result-cards">

                  {currentSequence.map(
                    (card) => (
                      <PlayingCard
                        key={card.id}
                        card={card}
                        small
                      />
                    )
                  )}

                </div>
              </div>

            </div>

            <div className="result-actions">

              {/* EXIT */}
              <button
                type="button"
                className="training-exit-result-btn"
                onClick={handleExit}
              >
                ← Exit
              </button>

              {/* TEST YOURSELF */}
              <button
                type="button"
                className="training-real-test-result-btn"
                onClick={handleRealTest}
              >
                🎯 Test Yourself
              </button>

              {/* TRY AGAIN */}
              <button
                type="button"
                className="retry-btn"
                onClick={handleRetry}
              >
                ↻ Try Again
              </button>

              {/* CHANGE CARDS */}
              <button
                type="button"
                className="secondary-large-btn"
                onClick={() =>
                  setPhase("settings")
                }
              >
                ⚙ Change Cards
              </button>

            </div>
          </>
        )}

      </Page>
    );
  }

  /* =====================================================
     SETTINGS
     ===================================================== */

  if (phase === "settings") {
    return (
      <Page className="settings-page">

        <div className="settings-icon">
          ⚙
        </div>

        <h1>
          Choose New Cards
        </h1>

        <p>
          Select a new number of cards
          to continue training.
        </p>

        <div className="settings-section">

          <h3>
            🃏 Choose Number of Cards
          </h3>

          <div className="settings-options">

            {[3, 4, 5, 6, 7, 8, 9, 10].map(
              (count) => (
                <button
                  key={count}
                  type="button"
                  className={
                    cardCount === count
                      ? "setting-option active"
                      : "setting-option"
                  }
                  onClick={() =>
                    handleChangeCards(
                      count
                    )
                  }
                >
                  {count}
                </button>
              )
            )}

          </div>

        </div>

        <div className="settings-section">

          <h3>
            ◷ Choose Display Time
          </h3>

          <div className="settings-options time-setting-options">

            {[7, 6, 5, 4, 3, 2].map(
              (time) => (
                <button
                  key={time}
                  type="button"
                  className={
                    displayTime ===
                    time
                      ? "setting-option active"
                      : "setting-option"
                  }
                  onClick={() =>
                    handleTimeChange(
                      time
                    )
                  }
                >
                  {time} sec
                </button>
              )
            )}

          </div>

        </div>

        <button
          type="button"
          className="continue-btn"
          onClick={
            handleContinueFromSettings
          }
        >
          ▶ Continue
        </button>

      </Page>
    );
  }

  return null;
};

/* =====================================================
   HEADER
   ===================================================== */

const TrainingHeader = ({
  round,
  cardCount,
  onExit,
  onRealTest,
}) => {
  return (
    <>
      <div className="training-top-actions">
        <button
          type="button"
          className="training-exit-btn"
          onClick={onExit}
        >
          ← Exit
        </button>

        <button
          type="button"
          className="training-real-test-btn"
          onClick={onRealTest}
        >
          🎯 Real Test
        </button>
      </div>

      <div className="training-game-header">

        <h1>
          Short Term Memory

          <span>
            Training
          </span>
        </h1>

      </div>

      <div className="round-info">

        <span>
          🏆 Round {round}
        </span>

        <span>
          🃏 {cardCount} Cards
        </span>

      </div>
    </>
  );
};

export default ShortTermMemoryTrainingGame;